import cn from "classnames";

import { useEffect, useMemo } from "react";

import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { LoadingButton } from "@mui/lab";
import { Fab, Grid, IconButton } from "@mui/material";

import { MAX_ROUTE_CITY_COUNT } from "@/constants/config";
import usePlatform from "@/hooks/usePlatform";

import useCondition, { ConditionType } from "../../hooks/useCondition";
import SelectLocation from "../SelectLocation";

interface SearchBarProps {
  onSearch?: (data: ConditionType) => void;
  onChange?: (data: ConditionType) => void;
}

const SearchBar = ({ onChange, onSearch }: SearchBarProps) => {
  const { isMobile } = usePlatform();
  const { condition, setCondition, onLocationSwapChange } = useCondition();

  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  useEffect(() => {
    onChange && onChange(condition);
  }, [condition, onChange]);

  const SearchBtn = useMemo(() => {
    return !isMobile ? (
      <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
        <LoadingButton
          color="primary"
          variant="contained"
          className="!min-w-[48px] !h-[56px] !w-[56px]"
          onClick={() => onSearch && onSearch(condition)}
        >
          <SearchIcon sx={{ fontSize: "24px" }} />
        </LoadingButton>
      </div>
    ) : null;
  }, [isMobile, onSearch, condition]);

  return (
    <div>
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{ paddingRight: !isMobile ? "64px" : 0 }}
      >
        <Grid item xs={12} sm={12}>
          <Grid container alignItems="center" rowGap={2}>
            <Grid item xs={12} sm={5.6}>
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where from?"
                Icon={<FlightTakeoffOutlinedIcon />}
                selectData={condition.fromLocation}
                onChange={(data) => {
                  setCondition({ ...condition, fromLocation: data });
                }}
              />
            </Grid>

            {!isMobile ? (
              <Grid item xs={1} sm={0.8} alignItems="center">
                <div className="flex items-center justify-center">
                  <IconButton
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                    disabled={disableSwap}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </div>
              </Grid>
            ) : (
              <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                <Fab color="primary" aria-label="swap" size="small" onClick={onLocationSwapChange}>
                  <SwapHorizIcon className="rotate-90" />
                </Fab>
              </div>
            )}

            <Grid item xs={12} sm={5.6}>
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where to?"
                Icon={<FlightLandOutlinedIcon />}
                selectData={condition.toLocation}
                onChange={(data) => {
                  setCondition({ ...condition, toLocation: data });
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {SearchBtn}
      </Grid>

      {isMobile ? (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            startIcon={<SearchIcon />}
            onClick={() => onSearch && onSearch(condition)}
          >
            Search
          </LoadingButton>
        </div>
      ) : null}
    </div>
  );
};

export default SearchBar;
